import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { QRCodeSVG } from "qrcode.react";
import jsQR from "jsqr";
import { ShieldCheck, Copy, Loader2, KeyRound, BadgeCheck, RefreshCw, QrCode, ScanLine, X, Camera, Atom } from "lucide-react";
import { QemCredit } from "@/components/common/QemCredit";
import { LiveproofSection } from "@/components/chat/LiveproofSection";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";

export function CipherLockDialog({ open, onOpenChange, cipher, onRefresh, verified, onVerify, quantumSeeded, chatId, userId, peerName }) {
  const state = cipher?.state;
  const loading = cipher == null;
  const [scanning, setScanning] = useState(false);
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const scanningRef = useRef(false);

  const stopScan = () => {
    scanningRef.current = false;
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = null;
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
    setScanning(false);
  };

  useEffect(() => stopScan, []);
  useEffect(() => {
    if (!open) stopScan();
  }, [open]);

  const copy = (val) => {
    if (!val) return;
    navigator.clipboard?.writeText(val);
    toast.success("Safety number copied");
  };

  const onScanned = (text) => {
    stopScan();
    if ((text || "").trim().toUpperCase() === (cipher?.fingerprint || "").toUpperCase()) {
      onVerify?.();
      toast.success("Safety numbers match — verified");
    } else {
      toast.error("Codes don't match — do not trust this session");
    }
  };

  const scanLoop = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!scanningRef.current || !video || !canvas) return;
    if (video.readyState === video.HAVE_ENOUGH_DATA) {
      canvas.width = video.videoWidth;
      canvas.height = video.videoHeight;
      const ctx = canvas.getContext("2d", { willReadFrequently: true });
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
      const img = ctx.getImageData(0, 0, canvas.width, canvas.height);
      const code = jsQR(img.data, img.width, img.height, { inversionAttempts: "dontInvert" });
      if (code?.data) {
        onScanned(code.data);
        return;
      }
    }
    rafRef.current = requestAnimationFrame(scanLoop);
  };

  const startScan = async () => {
    setScanning(true);
    scanningRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
      streamRef.current = stream;
      await new Promise((r) => setTimeout(r, 60));
      const video = videoRef.current;
      video.srcObject = stream;
      await video.play();
      rafRef.current = requestAnimationFrame(scanLoop);
    } catch {
      stopScan();
      toast.error("Couldn't access the camera. Compare the numbers manually instead.");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md border-white/10 bg-bloq-s1 text-white" data-testid="cipherlock-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-heading">
            <ShieldCheck className="h-5 w-5 text-bloq-mint" /> Cipher Lock
          </DialogTitle>
          <DialogDescription className="text-bloq-muted">
            Post-quantum end-to-end encryption for this conversation.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-10 text-bloq-muted">
            <Loader2 className="h-6 w-6 animate-spin" />
          </div>
        ) : state === "active" ? (
          <div className="space-y-4">
            <div className="flex items-center gap-3 rounded-xl border border-bloq-mint/30 bg-bloq-mint/10 p-3.5" data-testid="cipherlock-active">
              <ShieldCheck className="h-5 w-5 shrink-0 text-bloq-mint" />
              <div>
                <div className="text-sm font-semibold text-bloq-mint">Quantum-secured &amp; active</div>
                <div className="text-xs text-bloq-muted">ML-KEM-768 + X25519 hybrid session · XChaCha20-Poly1305</div>
              </div>
            </div>

            {cipher.signed && (
              <div className="flex items-center gap-2 rounded-xl border border-bloq-blue/30 bg-bloq-blue/10 px-3.5 py-2.5 text-xs text-bloq-blue" data-testid="cipherlock-signed">
                <BadgeCheck className="h-4 w-4 shrink-0" />
                Peer identity key signed with ML-DSA-65 (Dilithium) — protects against key substitution.
              </div>
            )}

            {quantumSeeded && (
              <div className="flex items-center gap-2.5 rounded-xl border border-bloq-purple/30 bg-bloq-purple/10 px-3 py-2.5" data-testid="quantum-seeded-pill">
                <Atom className="h-4 w-4 shrink-0 text-bloq-purple" />
                <div className="text-xs leading-snug">
                  <span className="font-semibold text-bloq-purple">QRNG Active</span>
                  <span className="text-bloq-muted"> · your identity keys were generated with QeM quantum entropy</span>
                </div>
              </div>
            )}
            {quantumSeeded && (
              <div className="flex justify-center"><QemCredit /></div>
            )}
            {chatId && userId && open && <LiveproofSection chatId={chatId} userId={userId} sessionId={cipher.sessionId} peerName={peerName} />}

            <div>
              <div className="mb-1 flex items-center gap-1.5 text-xs font-bold uppercase tracking-wider text-bloq-muted">
                <KeyRound className="h-3.5 w-3.5" /> Safety number
              </div>
              <button
                onClick={() => copy(cipher.fingerprint)}
                className="flex w-full items-center justify-between rounded-xl border border-white/10 bg-bloq-bg px-4 py-3 font-mono text-lg tracking-widest transition-colors hover:border-bloq-mint/30"
                data-testid="cipherlock-safety-number"
              >
                <span>{cipher.fingerprint || "—"}</span>
                <Copy className="h-4 w-4 text-bloq-muted" />
              </button>
            </div>

            <canvas ref={canvasRef} className="hidden" />

            {scanning ? (
              <div className="space-y-2" data-testid="cipherlock-scanner">
                <div className="relative overflow-hidden rounded-xl border border-bloq-mint/30 bg-black">
                  <video ref={videoRef} className="h-56 w-full object-cover" autoPlay playsInline muted />
                  <div className="pointer-events-none absolute inset-6 rounded-lg border-2 border-bloq-mint/70" />
                </div>
                <button onClick={stopScan} className="flex w-full items-center justify-center gap-1.5 rounded-full border border-white/12 py-2 text-xs font-semibold text-bloq-muted hover:text-white" data-testid="cipherlock-scan-cancel">
                  <X className="h-3.5 w-3.5" /> Cancel scan
                </button>
              </div>
            ) : verified ? (
              <div className="flex items-center gap-2 rounded-xl border border-bloq-mint/40 bg-bloq-mint/15 px-3.5 py-3 text-sm font-semibold text-bloq-mint" data-testid="cipherlock-verified">
                <BadgeCheck className="h-5 w-5 shrink-0" /> You verified this safety number.
              </div>
            ) : (
              <div className="space-y-3">
                <div className="flex flex-col items-center gap-2 rounded-xl border border-white/10 bg-white p-4">
                  <QRCodeSVG value={cipher.fingerprint || ""} size={144} level="M" data-testid="cipherlock-qr" />
                  <span className="flex items-center gap-1 text-[11px] font-semibold text-bloq-bg/70"><QrCode className="h-3 w-3" /> Have your contact scan this</span>
                </div>
                <div className="grid grid-cols-2 gap-2">
                  <button onClick={startScan} className="flex items-center justify-center gap-1.5 rounded-full border border-bloq-blue/40 bg-bloq-blue/10 py-2.5 text-xs font-bold text-bloq-blue transition-colors hover:bg-bloq-blue/15" data-testid="cipherlock-scan-btn">
                    <ScanLine className="h-4 w-4" /> Scan their code
                  </button>
                  <button onClick={() => onVerify?.()} className="flex items-center justify-center gap-1.5 rounded-full bloq-gradient py-2.5 text-xs font-bold text-bloq-bg transition-opacity hover:opacity-90" data-testid="cipherlock-mark-verified">
                    <BadgeCheck className="h-4 w-4" /> Mark verified
                  </button>
                </div>
                <p className="flex items-start gap-1.5 text-[11px] text-bloq-muted">
                  <Camera className="mt-0.5 h-3 w-3 shrink-0" />
                  Scan your contact's code (or compare the numbers in person) to confirm no one is intercepting your messages.
                </p>
              </div>
            )}
          </div>
        ) : state === "pending" ? (
          <div className="space-y-3" data-testid="cipherlock-pending">
            <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-4 text-sm text-amber-300">
              Waiting for the other person to publish their encryption keys. Messages will be end-to-end
              encrypted automatically as soon as they do.
            </div>
            <button onClick={onRefresh} className="flex items-center gap-2 rounded-full border border-white/12 px-3.5 py-2 text-xs font-semibold text-bloq-muted transition-colors hover:text-white" data-testid="cipherlock-refresh">
              <RefreshCw className="h-3.5 w-3.5" /> Check again
            </button>
          </div>
        ) : (
          <div className="space-y-3" data-testid="cipherlock-off">
            <div className="rounded-xl border border-white/8 bg-bloq-s2 p-4 text-sm text-bloq-muted">
              Cipher Lock is temporarily unavailable for this chat. Your messages are still protected in transit.
            </div>
            <button onClick={onRefresh} className="flex items-center gap-2 rounded-full border border-white/12 px-3.5 py-2 text-xs font-semibold text-bloq-muted transition-colors hover:text-white" data-testid="cipherlock-refresh">
              <RefreshCw className="h-3.5 w-3.5" /> Retry
            </button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
